import { identity, isEmpty, pickBy } from 'lodash-es';
import { local, session } from 'store2';
import { call, fork, put, take, takeEvery } from 'redux-saga/effects';
import { push as Push } from 'redux-first-history';

import Auth from '@api/services/Authentication';
import { LoginUserAction, SessionAuthWithDisposableTokenAction } from '@api/store/session/types';
import { AccountStartLoading } from '@api/store/account/actions';
import { LoadProductList } from '@api/store/products/actions';
import { authWithDisposableToken, login } from '@api/services/ElasticApi';
import { LoginUserError, LoginUserSuccess, SessionFailedAuth, SessionSuccessAuth } from '@api/store/session/actions';
import { ShowToast } from '@api/store/toast/actions';
import { ProductActionTypes } from '@api/store/products/types';
import { StatisticsPaths } from '@api/services/dictionaries/paths';
import { ProductCode } from '@dev/ee-api/ee-api-models';
import { GetDashboardSettings } from '@api/store/dashboardSettings/actions';
import { Logger } from '@api/services/errors/appLogger';

function* dispatchAction(): any {
    yield put(AccountStartLoading());
    yield put(GetDashboardSettings());
}

function* loginUser(action: LoginUserAction): any {
    const {
        payload: { UserName, Password, TwoFactorAuthCode, rememberMe, AuthProviderToken, GoogleIdToken },
        meta,
    } = action;
    const allLoginParams = {
        UserName,
        Password,
        TwoFactorAuthCode,
        AuthProviderToken,
        GoogleIdToken,
        Plan: ProductCode.EmailApi,
    };
    try {
        const auth = yield call(login, pickBy(allLoginParams, identity));
        if (auth && auth.ApiKey) {
            rememberMe ? local.set('token', auth.ApiKey) : session.set('token', auth.ApiKey);
            yield put(LoadProductList());
            yield take(ProductActionTypes.LOAD_PRODUCT_LIST_SUCCESS);
        }

        yield put(LoginUserSuccess(auth.Status, meta));
    } catch (e) {
        yield put(LoginUserError(e?.response?.data, meta));
        const errorMsg = e?.response?.data?.Error ? e.response.data.Error : e.message;

        Logger.error(errorMsg);
    }
}

function* startNewSession(action: SessionAuthWithDisposableTokenAction): any {
    const { token } = action.payload;
    let key;
    try {
        const resp = yield call(authWithDisposableToken, token);
        key = resp.data.ApiKey;

        Auth.setAccessToken(key);

        let pathName = StatisticsPaths.StatisticsOverview;
        if (isEmpty(sessionStorage.getItem('returnUrl'))) {
            pathName = sessionStorage.getItem('returnUrl');
            sessionStorage.removeItem('returnUrl');
        }
        yield put(SessionSuccessAuth());
        yield put(Push(pathName));
    } catch (e) {
        yield put(SessionFailedAuth());
        Logger.error(e);
        const errorMsg = e?.response?.data?.Error ? e.response.data.Error : e.message;
        yield put(
            ShowToast({
                message: `Error: ${errorMsg}`,
                severity: 'error',
                duration: 6000,
            })
        );
    }
}

function* watchAuthSuccessRequest(): any {
    yield takeEvery('@@session/AUTH_SUCCESS', dispatchAction);
}
function* loginUserWatcher(): any {
    yield takeEvery('@@session/LOGIN', loginUser);
}
function* watchAuth(): any {
    yield takeEvery('@@session/DISPOSABLE_TOKEN_AUTH_START', startNewSession);
}
const sessionSagas = [fork(watchAuthSuccessRequest), fork(loginUserWatcher), fork(watchAuth)];

export default sessionSagas;
